// React
import * as React from "react";
import { useState, useEffect, useContext } from "react";
import { styled } from "@mui/material/styles";
import AuthContext from "../../../../contexts/AuthContext";

import {
  Box,
  Button,
  CssBaseline,
  Grid,
  IconButton,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import Drawer from "@mui/material/Drawer";
import AppBar from "@mui/material/AppBar";

// Icons
import MenuIcon from "@mui/icons-material/Menu";
import LogoutIcon from "@mui/icons-material/Logout";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import LightModeIcon from "@mui/icons-material/LightMode";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";

import { Outlet, useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import BaseSideBar from "./sidebar/BaseSideBar";
import { useThemeMode } from "../../../../contexts/ThemeContext";
import VittaDark from "../../../../images/VittaDarkMode.png";
import VittaLight from "../../../../images/VittaLightMode.png";

export default function PersistentDrawerLeft() {
  // States
  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);

  // Uses
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { mode, toggleMode } = useThemeMode();
  const { userLoggedData, logout, isAuthLoading } = useContext(AuthContext);

  // useEffects
  useEffect(() => {
    if (isAuthLoading) {
      console.log("ta loading");
      return;
    }
    if (!userLoggedData || userLoggedData?.role.toUpperCase() !== "ADMIN") {
      navigate("/login");
    }
  }, [userLoggedData, isAuthLoading, navigate]);

  useEffect(() => {
    if (!isMobile) {
      setMobileOpen(false);
    }
  }, [isMobile]);

  const handleDrawerToggle = () => {
    if (isMobile) {
      setMobileOpen((prev) => !prev);
      return;
    }
    setSidebarExpanded((prev) => !prev);
  };

  // await data loading
  if (!userLoggedData) return null;

  const drawerOpen = isMobile ? mobileOpen : true;
  const drawerCurrentWidth = isMobile
    ? drawerWidth
    : sidebarExpanded
      ? drawerWidth
      : miniDrawerWidth;
  const sidebarContentOpen = isMobile ? true : sidebarExpanded;
  const vittaLogo = mode === "dark" ? VittaDark : VittaLight;

  return (
    <Box
      sx={{
        width: "100%",
        height: "100dvh",
        display: "flex",
        flexDirection: "column",
        bgcolor: "background.default",
        overflow: "hidden",
      }}
    >
      <CssBaseline />

      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: "background.default",
          borderBottom: "none",
        }}
      >
        <Toolbar
          sx={{
            display: "flex",
            justifyContent: "space-between",
            gap: 2,
            minHeight: { xs: 64, sm: 72 },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
            <IconButton
              aria-label="abrir menu"
              onClick={handleDrawerToggle}
              edge="start"
              sx={{ mr: 1.5, display: isMobile ? "inline-flex" : "none" }}
            >
              <MenuIcon />
            </IconButton>
            <Box
              component="img"
              src={vittaLogo}
              alt="Logo Vitta"
              sx={{
                height: { xs: 34, sm: 38 },
                objectFit: "contain",
                mr: 1.4,
              }}
            />
            <Typography
              variant="h5"
              fontWeight="bold"
              sx={{
                fontFamily: "'Audiowide', cursive",
                letterSpacing: { xs: 1, sm: 2 },
                color: "text.primary",
                fontSize: { xs: "1.2rem", sm: "1.5rem" },
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            ></Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Tooltip title={mode === "dark" ? "Modo claro" : "Modo escuro"}>
              <IconButton
                aria-label="alternar tema"
                onClick={toggleMode}
                sx={{
                  bgcolor: "action.hover",
                  color: "text.primary",
                  "&:hover": { bgcolor: "action.selected" },
                }}
              >
                {mode === "dark" ? <LightModeIcon /> : <DarkModeIcon />}
              </IconButton>
            </Tooltip>
            <Button
              onClick={logout}
              variant="contained"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.25,
                bgcolor: "primary.main",
                color: "primary.contrastText",
                "&:hover": { bgcolor: "primary.dark" },
                px: { xs: 1.5, sm: 3 },
                minWidth: "auto",
              }}
            >
              <Typography
                variant="body2"
                sx={{ display: { xs: "none", sm: "block" } }}
              >
                Sair
              </Typography>
              <LogoutIcon fontSize="small" />
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      <Box sx={{ display: "flex", flex: 1, minHeight: 0, overflow: "hidden" }}>
        <Drawer
          sx={{
            width: drawerCurrentWidth,
            height: "100%",
            flexShrink: 0,
            "&.MuiDrawer-docked": {
              height: "100%",
              width: drawerCurrentWidth,
            },
            "& .MuiDrawer-paper": {
              width: drawerCurrentWidth,
              boxSizing: "border-box",
              border: "1px solid",
              borderColor: "divider",
              position: "relative",
              height: isMobile ? "100%" : "calc(100% - 16px)",
              mt: isMobile ? 0 : 1,
              ml: isMobile ? 0 : 1,
              borderRadius: isMobile ? 0 : "12px",
              overflowX: "hidden",
              bgcolor: "background.paper",
              transition: theme.transitions.create("width", {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.standard,
              }),
            },
          }}
          variant={isMobile ? "temporary" : "permanent"}
          anchor="left"
          open={drawerOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
        >
          <BaseSideBar
            logout={logout}
            userRole={userLoggedData.role || "N"}
            open={sidebarContentOpen}
            isMobile={isMobile}
            onToggleSidebar={handleDrawerToggle}
            LOGGED_USER_NAME={userLoggedData.mkUser}
          />
        </Drawer>

        <Main>
          <Box
            component="main"
            elevation={4}
            sx={{
              flex: 1,
              bgcolor: "background.default",
              minHeight: 0,
              borderRadius: { xs: 0, sm: "15px" },
              overflowY: "auto",
              overflowX: "hidden",
            }}
          >
            <Grid
              container
              sx={{
                p: { xs: 1.5, sm: 2, md: 3 },
              }}
            >
              <Grid item xs={12}>
                <Outlet />
              </Grid>
            </Grid>
          </Box>
        </Main>
      </Box>

      <Tooltip title="Suporte via WhatsApp" placement="left">
        <IconButton
          component="a"
          href="https://wa.me/54992065346?text=Ol%C3%A1%2C%20necessito%20de%20ajuda%20com%20o%20Apeak%20Club"
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            position: "fixed",
            bottom: 24,
            right: 24,
            bgcolor: "#25D366",
            color: "#fff",
            width: 56,
            height: 56,
            boxShadow: 4,
            zIndex: 1300,
            "&:hover": { bgcolor: "#1ebe5d" },
          }}
        >
          <WhatsAppIcon fontSize="medium" />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

// Theme

const drawerWidth = 240;
const miniDrawerWidth = 72;

const Main = styled("main")(({ theme }) => ({
  flexGrow: 1,
  minWidth: 0,
  minHeight: 0,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  padding: theme.spacing(2),
}));
